/**
 * ForgotPassword Component
 * 
 * Multi-step password reset flow with 4 steps:
 * 1. Enter email
 * 2. Enter verification code
 * 3. Set new password
 * 4. Success confirmation
 */

import React, { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { ArrowLeft, Mail, Lock, CheckCircle, Eye, EyeOff } from 'lucide-react'
import containerImage from '../assets/images/container-illustration.png'
import { authAPI } from '../services/api'

const ForgotPassword = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(1)
  const [email, setEmail] = useState('')
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', ''])
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Password strength indicators
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    lowercase: false,
    uppercase: false,
    number: false,
  })

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    if (!email) {
      setError('Please enter your email')
      return
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email')
      return
    }
    
    try {
      const response = await authAPI.forgotPassword(email)

      // Check if this is a Google OAuth account
      if (response && response.isGoogleAuth === true) {
        setError('This account uses Google Sign-In. Please use the \u201cSign in with Google\u201d button on the login page.')
        setTimeout(() => {
          navigate('/login')
        }, 3000)
        return
      }

      setCurrentStep(2)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to send reset email. Please try again.')
    }
  }

  const handleCodeChange = (index: number, value: string) => {
    if (value.length <= 1) {
      const newCode = [...verificationCode]
      newCode[index] = value
      setVerificationCode(newCode)
      
      // Auto-focus next input
      if (value && index < 5) {
        const nextInput = document.getElementById(`code-${index + 1}`)
        nextInput?.focus()
      }
    }
  }

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !verificationCode[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`)
      prevInput?.focus()
    }
  }

  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    const code = verificationCode.join('')
    if (code.length !== 6) {
      setError('Please enter the complete verification code')
      return
    }
    
    try {
      await authAPI.verifyResetCode(email, code)
      setCurrentStep(3)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid verification code. Please try again.')
    }
  }

  const checkPasswordStrength = (pwd: string) => {
    setPasswordStrength({
      length: pwd.length >= 8,
      lowercase: /[a-z]/.test(pwd),
      uppercase: /[A-Z]/.test(pwd),
      number: /[0-9]/.test(pwd),
    })
  }

  const handlePasswordChange = (value: string) => {
    setPassword(value)
    checkPasswordStrength(value)
  }

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (!/[A-Z]/.test(password)) {
      setError('Password must contain at least one uppercase letter')
      return
    }

    if (!/[a-z]/.test(password)) {
      setError('Password must contain at least one lowercase letter')
      return
    }

    if (!/[0-9]/.test(password)) {
      setError('Password must contain at least one number')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    
    try {
      const code = verificationCode.join('')
      await authAPI.resetPassword(email, code, password)
      setCurrentStep(4)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to reset password. Please try again.')
    }
  }

  const getStepIndicators = () => {
    return (
      <div className="flex gap-2">
        {[1, 2, 3, 4].map((step) => (
          <div
            key={step}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              step === currentStep
                ? 'w-8 bg-gray-800'
                : step < currentStep
                ? 'bg-gray-400'
                : 'bg-gray-200'
            }`}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="min-h-screen flex relative">
      {/* Logo - Top Left */}
      <div className="absolute top-6 left-6 z-10">
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="EAloom" className="w-7 h-7 rounded-md" />
          <h1 className="text-lg font-semibold text-gray-900">EAloom</h1>
        </div>
      </div>

      {/* Copyright - Bottom Left */}
      <div className="absolute bottom-6 left-6 z-10">
        <p className="text-xs text-gray-500 leading-relaxed">© 2026 EAloom. All rights reserved.</p>
        <p className="text-xs text-gray-400 leading-relaxed">EAloom is owned and operated by Lambda Flow LLC.</p>
      </div>

      {/* Left side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-4 sm:px-6 lg:px-20 xl:px-28">
        <div className="w-full max-w-sm">
          {/* Step 1: Enter Email */}
          {currentStep === 1 && (
            <div>
              <div className="mb-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                  <Mail className="w-6 h-6 text-gray-600" aria-hidden="true" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Forgot password?</h2>
                <p className="text-gray-600">No worries, we\u2019ll send you reset instructions.</p>
              </div>

              <div aria-live="polite" aria-atomic="true">
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
              </div>

              <form onSubmit={handleEmailSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your email
                  </label>
                  <div className="relative rounded-lg border-2 border-gray-300 focus-within:border-green-500 transition-colors">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      spellCheck={false}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email\u2026"
                      className="w-full h-11 px-3 border-0 bg-transparent focus:ring-0 focus:outline-none rounded-lg"
                      required
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Reset password
                </Button>

                <Link
                  to="/login"
                  className="w-full text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                  Back to log in
                </Link>
              </form>

              <div className="mt-8 flex justify-center">
                {getStepIndicators()}
              </div>
            </div>
          )}

          {/* Step 2: Enter Verification Code */}
          {currentStep === 2 && (
            <div>
              <div className="mb-8">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <Mail className="w-6 h-6 text-blue-600" aria-hidden="true" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Enter verification code</h2>
                <p className="text-gray-600">We sent a 6-digit code to {email}</p>
              </div>

              <div aria-live="polite" aria-atomic="true">
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
              </div>

              <form onSubmit={handleCodeSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-4">
                    Verification code
                  </label>
                  <div className="flex gap-2 justify-center">
                    {verificationCode.map((digit, index) => (
                      <input
                        key={index}
                        id={`code-${index}`}
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        aria-label={`Digit ${index + 1} of 6`}
                        value={digit}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleCodeKeyDown(index, e)}
                        className="w-12 h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none transition-colors"
                        maxLength={1}
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Verify code
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={async () => {
                      setError(null)
                      try {
                        await authAPI.forgotPassword(email)
                        setVerificationCode(['', '', '', '', '', ''])
                      } catch (err: unknown) {
                        setError(err instanceof Error ? err.message : 'Failed to resend code. Please try again.')
                      }
                    }}
                    className="text-sm text-green-600 hover:text-green-700 font-medium"
                  >
                    Send another code
                  </button>
                </div>
              </form>

              <div className="mt-8 flex justify-center">
                {getStepIndicators()}
              </div>
            </div>
          )}

          {/* Step 3: Set New Password */}
          {currentStep === 3 && (
            <div>
              <div className="mb-8">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                  <Lock className="w-6 h-6 text-gray-600" aria-hidden="true" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Set new password</h2>
                <p className="text-gray-600">Must be at least 8 characters with uppercase, lowercase, and digit.</p>
              </div>

              <div aria-live="polite" aria-atomic="true">
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}
              </div>

              <form onSubmit={handlePasswordSubmit} className="space-y-6">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative rounded-lg border-2 border-gray-300 focus-within:border-green-500 transition-colors">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => handlePasswordChange(e.target.value)}
                      placeholder="Enter your password\u2026"
                      className="w-full h-11 px-3 pr-10 border-0 bg-transparent focus:ring-0 focus:outline-none rounded-lg"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" aria-hidden="true" />
                      ) : (
                        <Eye className="w-5 h-5" aria-hidden="true" />
                      )}
                    </button>
                  </div>

                  {/* Password strength indicators */}
                  <div className="mt-3 flex gap-2">
                    <div className={`h-1 flex-1 rounded-full ${passwordStrength.length ? 'bg-green-500' : 'bg-gray-200'}`} />
                    <div className={`h-1 flex-1 rounded-full ${passwordStrength.lowercase && passwordStrength.uppercase ? 'bg-green-500' : 'bg-gray-200'}`} />
                    <div className={`h-1 flex-1 rounded-full ${passwordStrength.number ? 'bg-green-500' : 'bg-gray-200'}`} />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm password
                  </label>
                  <div className="relative rounded-lg border-2 border-gray-300 focus-within:border-green-500 transition-colors">
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password\u2026"
                      className="w-full h-11 px-3 pr-10 border-0 bg-transparent focus:ring-0 focus:outline-none rounded-lg"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-5 h-5" aria-hidden="true" />
                      ) : (
                        <Eye className="w-5 h-5" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Reset password
                </Button>

                <Link
                  to="/login"
                  className="w-full text-sm text-gray-600 hover:text-gray-900 font-medium flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" aria-hidden="true" />
                  Back to log in
                </Link>
              </form>

              <div className="mt-8 flex justify-center">
                {getStepIndicators()}
              </div>
            </div>
          )}

          {/* Step 4: Success */}
          {currentStep === 4 && (
            <div>
              <div className="mb-8">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-6 h-6 text-green-600" aria-hidden="true" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">All done!</h2>
                <p className="text-gray-600">Your password has been reset successfully.</p>
              </div>

              <div>
                <Link
                  to="/login"
                  className="w-full h-11 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200 flex items-center justify-center"
                >
                  Back to Login
                </Link>
              </div>

              <div className="mt-8 flex justify-center">
                {getStepIndicators()}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right side - Container image */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-blue-50 to-indigo-100 overflow-hidden">
        {/* Container illustration */}
        <div className="flex items-center justify-center w-full h-full relative">
          <img
            src={containerImage}
            alt="Container Illustration"
            width={800}
            height={600}
            className="w-full h-full max-w-none max-h-none object-contain scale-150"
          />
        </div>

        {/* Decorative elements */}
        <div className="absolute top-20 right-20 w-32 h-32 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-32 left-20 w-24 h-24 bg-blue-200/20 rounded-full blur-lg"></div>
      </div>
    </div>
  )
}

export default ForgotPassword